import { MessageDescriptor } from 'react-intl';

import ContentBasedImage from '@/../public/_n/images/dtc-partners/ContentDevice.png';
import DisneyContentBasedImage from '@/../public/_n/images/dtc-partners/DisneyContentDevice.png';
import { PartnerDetails } from '@/components/dtcPartnerships/shared/types';
import AdditionalContent from '@/components/SimplifiedSignup/Components/AdditionalContent';
import { CopyItem } from '@/components/valuePropList';
import { _getDTCOfferHeader as getDTCOfferHeader } from '@/hooks/dtcLeadGenPartnership/useDTCPartners';
import leadGenMessages from '@/hooks/dtcLeadGenPartnership/useDTCPartners/messages';

import Account from '../../Account';
import generalMessages from '../../Account/messages';
import { AccountScreenConfig } from '../../Account/types';
import Handoff from '../../Handoff';
import { HandoffScreenConfig } from '../../Handoff/types';
import Payment from '../../Payment';
import { PaymentScreenConfig } from '../../Payment/types';
import { selectStandardOfferTerms } from '../../utils/offerTerms';
import ControlScreenConfigs from '../Control/Screens';
import partnerMessages from './messages';

const activeScreenSet = {
	account: Account as React.FC,
	payment: Payment as React.FC,
	handoff: Handoff as React.FC,
};

export const accountValueProps: Array<CopyItem> = [
	{
		id: 'valueProp1',
		header: generalMessages.valueProp1Head,
		body: generalMessages.valueProp1Body,
	},
	{
		id: 'valueProp2',
		header: generalMessages.valueProp2Head,
		body: generalMessages.valueProp2Body,
	},
	{
		id: 'valueProp3',
		header: generalMessages.valueProp3Head,
		body: generalMessages.valueProp3Body,
	},
];

const PartnerAccountScreenConfig = (
	partner: PartnerDetails | null,
	formatMessage: (
		message: MessageDescriptor,
		placeholders?: Record<string, string | number | undefined>,
	) => string,
): AccountScreenConfig => {
	const partnerAuthTerms = selectStandardOfferTerms(
		Boolean(partner?.offer_details?.trial_duration),
		Boolean(partner?.offer_details?.percent_off),
	);
	// Disney+ Perks uses a different content based image
	const contentBasedImageSrc =
		partner?.slug === 'disneyplusperks' ? DisneyContentBasedImage : ContentBasedImage;
	return {
		...ControlScreenConfigs.account,
		upsellHeader: () => ({ message: partnerMessages.valuePropHeader }),
		loginTerms: partnerAuthTerms,
		signupTerms: partnerAuthTerms,
		heading: () => {
			if (!partner) return { message: generalMessages.signupFormHeading };
			const trialDuration: MessageDescriptor | null = partner.offer_details?.trial_duration
				? leadGenMessages[partner.offer_details?.trial_duration]
				: null;
			return {
				message: getDTCOfferHeader(partner),
				placeholders: {
					partnerName: partner.name,
					duration: trialDuration ? formatMessage(trialDuration) : '',
					percentOff: partner.offer_details?.percent_off || '',
					discountPercent: partner.offer_details?.percent_off || '',
					interval: partner.offer_details?.billing_plan?.includes('1m')
						? formatMessage(partnerMessages.month, {
								quantity: partner.offer_details?.duration_in_months || 1,
							})
						: formatMessage(partnerMessages.year, {
								quantity: partner.offer_details?.duration_in_months
									? Math.ceil((partner.offer_details?.duration_in_months as number) / 12)
									: 1,
							}),
				},
			};
		},
		showHeader: false,
		limitedTimeOffer: false,
		terms: partner?.offer_details?.terms || '',
		AdditionalContent: () => <AdditionalContent />,
		contentBasedImage: {
			src: contentBasedImageSrc,
		},
	};
};

const PartnerPaymentScreenConfig = (
	partner: PartnerDetails | null,
	formatMessage: (
		message: MessageDescriptor,
		placeholders?: Record<string, string | number | undefined>,
	) => string,
): PaymentScreenConfig => {
	return {
		...ControlScreenConfigs.payment,
		component: activeScreenSet.payment,
		name: 'payment',
		nextScreen: 'handoff',
		showHeader: false,
		upsellBullets: accountValueProps,
		basicLineHeader: undefined,
		heading: () => {
			if (!partner) return { message: generalMessages.signupFormHeading };
			const trialDuration: MessageDescriptor | null = partner.offer_details?.trial_duration
				? leadGenMessages[partner.offer_details?.trial_duration]
				: null;
			return {
				message: getDTCOfferHeader(partner),
				placeholders: {
					partnerName: partner.name,
					duration: trialDuration ? formatMessage(trialDuration) : '',
					percentOff: partner.offer_details?.percent_off || '',
					discountPercent: partner.offer_details?.percent_off || '',
					interval: partner.offer_details?.billing_plan?.includes('1m')
						? formatMessage(partnerMessages.month, {
								quantity: partner.offer_details?.duration_in_months || 1,
							})
						: formatMessage(partnerMessages.year, {
								quantity: partner.offer_details?.duration_in_months
									? Math.ceil((partner.offer_details?.duration_in_months as number) / 12)
									: 1,
							}),
				},
			};
		},
		showToggle: false,
		limitedTimeOffer: false,
		userCanExit: false,
		hidePaypal: true,
	};
};
const PartnerHandoffScreenConfig = (): HandoffScreenConfig => {
	return {
		...ControlScreenConfigs.handoff,
	};
};

export const generatePartnerScreenConfigs = (
	partner: PartnerDetails | null,
	formatMessage: (
		message: MessageDescriptor,
		placeholders?: Record<string, string | number | undefined>,
	) => string,
) => {
	return {
		account: PartnerAccountScreenConfig(partner, formatMessage),
		payment: PartnerPaymentScreenConfig(partner, formatMessage),
		handoff: PartnerHandoffScreenConfig(),
	};
};

export default {
	account: PartnerAccountScreenConfig,
	payment: PartnerPaymentScreenConfig,
	handoff: PartnerHandoffScreenConfig,
};
